import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  message,
  Descriptions,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Spin,
  Radio,
  Table,
  Collapse,
} from "antd";
import {
  ApiOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DisconnectOutlined,
  LinkOutlined,
  ReloadOutlined,
  ImportOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { Company } from "../types/company";
import {
  PicqerService,
  PicqerFulfilmentCustomer,
} from "../services/picqerService";
import { PicqerProductImport } from "./PicqerProductImport";
import { supabase } from "../lib/supabase";

const { Text } = Typography;
const { Option } = Select;

type ChannelDockSeller = { id: number; company?: string; first_name?: string; last_name?: string; status?: string };

function channelDockSellerLabel(s: ChannelDockSeller): string {
  const name = s.company?.trim() || [s.first_name, s.last_name].filter(Boolean).join(" ").trim();
  return name ? `${name} (#${s.id})` : `Seller #${s.id}`;
}

/** ChannelDock API key + secret, save to company. Does not change wms_provider; use Active WMS switch. Layout matches Picqer card. */
function ChannelDockIntegrationCard({
  company,
  onRefresh,
  onImportProducts,
  linkedProductCount,
  dashboardProductTotal,
  wmsTotalCount,
  wmsTotalCountLoading,
  onProductsCountClick,
}: {
  company: Company;
  onRefresh: () => void;
  onImportProducts?: () => void;
  linkedProductCount?: number;
  /** All catalog products for this company (My Products page). */
  dashboardProductTotal?: number;
  wmsTotalCount?: number;
  wmsTotalCountLoading?: boolean;
  onProductsCountClick?: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [sellers, setSellers] = useState<ChannelDockSeller[]>([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellersError, setSellersError] = useState<string | null>(null);
  const hasCredentials =
    company.channeldock_enabled && !!company.channeldock_api_key;

  const MASK = "********";

  const fetchSellers = async () => {
    setSellersLoading(true);
    setSellersError(null);
    try {
      const { data, error } = await supabase.functions.invoke("channeldock-center-sellers");
      if (error) throw error;
      const result = data as { success?: boolean; data?: { sellers?: ChannelDockSeller[] }; error?: string };
      if (!result?.success || !result.data?.sellers) {
        throw new Error(result?.error ?? "Failed to load sellers");
      }
      setSellers(result.data.sellers);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load ChannelDock sellers";
      setSellersError(msg);
      setSellers([]);
    } finally {
      setSellersLoading(false);
    }
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  useEffect(() => {
    if (showConfigModal) {
      form.setFieldsValue({
        channeldock_api_key: company.channeldock_api_key ? MASK : "",
        channeldock_api_secret: company.channeldock_api_secret ? MASK : "",
        channeldock_seller_id: company.channeldock_seller_id ?? undefined,
      });
    }
  }, [showConfigModal, company.id, company.channeldock_api_key, company.channeldock_api_secret, company.channeldock_seller_id, form]);

  const handleTestConnection = async () => {
    if (!company.id) return;
    if (!company.channeldock_api_key || !company.channeldock_api_secret) {
      message.error("Save API key and secret first, then test connection.");
      return;
    }
    setTestingConnection(true);
    try {
      const { data, error } = await supabase.functions.invoke("channeldock-test-connection", {
        body: { company_id: company.id },
      });
      if (error) throw error;
      if (data?.success) {
        message.success("ChannelDock connection test successful!");
      } else {
        message.error(data?.error ?? "Connection test failed");
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Connection test failed");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!company.id) return;
    try {
      const values = await form.validateFields();
      setLoading(true);
      const newKey =
        values.channeldock_api_key?.trim() && values.channeldock_api_key?.trim() !== MASK
          ? values.channeldock_api_key.trim()
          : company.channeldock_api_key ?? null;
      const newSecret =
        values.channeldock_api_secret?.trim() && values.channeldock_api_secret?.trim() !== MASK
          ? values.channeldock_api_secret.trim()
          : company.channeldock_api_secret ?? null;
      const hasNewCreds = !!(newKey && newSecret);
      const rawSellerId = values.channeldock_seller_id;
      const sellerId =
        rawSellerId !== undefined && rawSellerId !== null && rawSellerId !== "" && !Number.isNaN(Number(rawSellerId))
          ? Number(rawSellerId)
          : null;
      const { error } = await supabase
        .from("company")
        .update({
          channeldock_api_key: newKey,
          channeldock_api_secret: newSecret,
          channeldock_enabled: hasNewCreds,
          channeldock_seller_id: sellerId ?? null,
        })
        .eq("id", company.id);
      if (error) throw error;
      message.success("ChannelDock credentials saved.");
      setShowConfigModal(false);
      onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: "Disconnect ChannelDock",
      content: "Clear ChannelDock credentials and disable for this company?",
      okText: "Disconnect",
      okType: "danger",
      onOk: async () => {
        if (!company.id) return;
        setLoading(true);
        try {
          const { error } = await supabase
            .from("company")
            .update({
              channeldock_api_key: null,
              channeldock_api_secret: null,
              channeldock_enabled: false,
              channeldock_seller_id: null,
              wms_provider: company.wms_provider === "channeldock" ? null : company.wms_provider,
            })
            .eq("id", company.id);
          if (error) throw error;
          message.success("ChannelDock disconnected.");
          setShowConfigModal(false);
          onRefresh();
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleOpenConfig = () => setShowConfigModal(true);

  const currentSellerLabel = (() => {
    if (company.channeldock_seller_id == null) return null;
    const s = sellers.find((x) => x.id === company.channeldock_seller_id);
    return s ? channelDockSellerLabel(s) : `Seller #${company.channeldock_seller_id}`;
  })();

  return (
    <>
      {!hasCredentials && (
        <Alert
          message="ChannelDock Not Connected"
          description="This company is not connected to ChannelDock. Set up the integration to enable WMS features."
          type="info"
          showIcon
          icon={<ApiOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <img src="/images/channeldock-svg.png" alt="ChannelDock" style={{ height: 24, width: "auto", display: "block" }} />
            ChannelDock Integration Settings
            {hasCredentials && <Tag color="green">Connected</Tag>}
          </Space>
        }
        size="small"
        style={{ marginTop: 0 }}
      >
        <Space wrap style={{ marginBottom: 16 }}>
          {hasCredentials && (
            <>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleTestConnection}
                loading={testingConnection}
              >
                Test Connection
              </Button>
              {onImportProducts && (
                <Button icon={<ImportOutlined />} onClick={onImportProducts}>
                  Import/Link from ChannelDock
                </Button>
              )}
            </>
          )}
          <Button
            type="primary"
            icon={hasCredentials ? <SettingOutlined /> : <LinkOutlined />}
            onClick={handleOpenConfig}
          >
            {hasCredentials ? "Configure" : "Connect to ChannelDock"}
          </Button>
          {hasCredentials && (
            <Button
              danger
              icon={<DisconnectOutlined />}
              onClick={handleDisconnect}
              loading={loading}
            >
              Disconnect
            </Button>
          )}
        </Space>
        {hasCredentials ? (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="API Key" span={2}>
              <Text code>
                {company.channeldock_api_key
                  ? `${company.channeldock_api_key.substring(0, 8)}...`
                  : "Not configured"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color="green">Connected</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Enabled">
              {company.channeldock_enabled ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="ChannelDock Seller" span={2}>
              {currentSellerLabel ? (
                <Space>
                  <Text strong>{currentSellerLabel}</Text>
                  <a
                    href={`https://channeldock.com/portal/public/centersellers?seller_id=${company.channeldock_seller_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkOutlined /> Open in ChannelDock
                  </a>
                </Space>
              ) : (
                <Text type="secondary">Not set</Text>
              )}
            </Descriptions.Item>
            {dashboardProductTotal != null && (
              <Descriptions.Item label="Total my products (dashboard)" span={2}>
                <Text strong>{dashboardProductTotal}</Text>
                <Text type="secondary"> product{dashboardProductTotal !== 1 ? "s" : ""}</Text>
              </Descriptions.Item>
            )}
            {linkedProductCount != null && (
              <Descriptions.Item label="My Products linked" span={2}>
                <Text strong>{linkedProductCount}</Text>
                <Text type="secondary"> product{linkedProductCount !== 1 ? "s" : ""}</Text>
              </Descriptions.Item>
            )}
            {(wmsTotalCountLoading || wmsTotalCount != null) && (
              <Descriptions.Item label="Products in ChannelDock" span={2}>
                {wmsTotalCountLoading && wmsTotalCount == null ? (
                  <Spin size="small" />
                ) : wmsTotalCount != null && onProductsCountClick ? (
                  <Text
                    type="secondary"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={onProductsCountClick}
                  >
                    <Text strong>{wmsTotalCount}</Text>
                    {" product"}{wmsTotalCount !== 1 ? "s" : ""}
                  </Text>
                ) : wmsTotalCount != null ? (
                  <>
                    <Text strong>{wmsTotalCount}</Text>
                    <Text type="secondary"> product{wmsTotalCount !== 1 ? "s" : ""}</Text>
                  </>
                ) : null}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
          >
            <ApiOutlined
              style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
            />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No ChannelDock Integration
            </div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>
              Connect this company to ChannelDock to enable WMS orders,
              inventory sync, and fulfillment features.
            </div>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleOpenConfig}
            >
              Set Up ChannelDock Integration
            </Button>
          </div>
        )}
      </Card>

      {/* Configure / Set up modal – same pattern as Picqer */}
      <Modal
        title={hasCredentials ? "Configure ChannelDock Integration" : "Set Up ChannelDock Integration"}
        open={showConfigModal}
        onCancel={() => setShowConfigModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowConfigModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={handleSave}
          >
            {hasCredentials ? "Update Integration" : "Connect to ChannelDock"}
          </Button>,
        ]}
        width={560}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
          Log in to ChannelDock as this company (seller), go to <strong>Settings → API &amp; Webhooks</strong> to create or copy your API key and secret.{" "}
          You must select the ChannelDock seller that matches this company — required for Center inventory, product import, and purchase orders.
        </Text>
        <Form form={form} layout="vertical">
          <Form.Item
            name="channeldock_seller_id"
            label="ChannelDock Seller"
            rules={[{ required: true, message: "Select the ChannelDock seller for this company" }]}
            help="Pick the seller from the fulfillment center list (same account as the API credentials above)."
          >
            <Select
              placeholder="Select seller…"
              loading={sellersLoading}
              showSearch
              optionFilterProp="label"
              dropdownRender={(menu) => (
                <>
                  {sellersError && (
                    <div style={{ padding: "8px 12px", color: "var(--ant-color-error)" }}>
                      {sellersError}
                    </div>
                  )}
                  {menu}
                </>
              )}
            >
              {sellers.map((s) => (
                <Option key={s.id} value={s.id} label={channelDockSellerLabel(s)}>
                  {channelDockSellerLabel(s)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="channeldock_api_key"
            label="ChannelDock API Key"
            rules={[{ required: !company.channeldock_api_key, message: "Enter API key" }]}
          >
            <Input.Password placeholder="API key from ChannelDock" autoComplete="off" visibilityToggle={false} />
          </Form.Item>
          <Form.Item
            name="channeldock_api_secret"
            label="ChannelDock API Secret"
            rules={[{ required: !company.channeldock_api_secret, message: "Enter API secret" }]}
          >
            <Input.Password placeholder="API secret from ChannelDock" autoComplete="off" visibilityToggle={false} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

interface AdminPicqerIntegrationSectionProps {
  company: Company;
  onRefresh: () => void;
}

const AdminPicqerIntegrationSection: React.FC<
  AdminPicqerIntegrationSectionProps
> = ({ company, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [savingWmsSwitch, setSavingWmsSwitch] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importModalWmsOverride, setImportModalWmsOverride] = useState<"picqer" | "channeldock" | null>(null);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [apiKeyName, setApiKeyName] = useState("");
  const [form] = Form.useForm();
  type CdGetEndpointKey = "list_orders" | "list_products" | "stock";
  const [cdTestLoading, setCdTestLoading] = useState<CdGetEndpointKey | null>(null);
  const [cdTestResults, setCdTestResults] = useState<
    Record<string, { success: boolean; count?: number; error?: string; raw?: unknown }>
  >({});
  const [showCdResultModal, setShowCdResultModal] = useState(false);
  const [linkedProductCounts, setLinkedProductCounts] = useState<{
    picqer: number;
    channeldock: number;
  } | null>(null);
  /** Total rows in company_product (same as customer My Products). */
  const [dashboardProductTotal, setDashboardProductTotal] = useState<number | null>(null);
  const [wmsTotalCounts, setWmsTotalCounts] = useState<{
    picqer: number | null;
    channeldock: number | null;
  }>({ picqer: null, channeldock: null });
  const [wmsTotalCountsLoading, setWmsTotalCountsLoading] = useState(false);
  const [productsModalWms, setProductsModalWms] = useState<"picqer" | "channeldock" | null>(null);
  const [productsModalLoading, setProductsModalLoading] = useState(false);
  const [productsModalData, setProductsModalData] = useState<{
    products: Array<{ id: number; productcode: string; name: string; ean?: string }>;
    linkedIds: number[];
  } | null>(null);

  const hasPicqerCredentials =
    !!company.picqer_enabled &&
    !!company.picqer_api_key &&
    !!company.picqer_fulfilment_customer_id;
  const hasChannelDockCredentials =
    !!company.channeldock_enabled &&
    !!company.channeldock_api_key;

  const handleWmsProviderChange = async (value: "none" | "picqer" | "channeldock") => {
    if (!company.id) return;
    const wmsProvider = value === "none" ? null : value;
    setSavingWmsSwitch(true);
    try {
      const { error } = await supabase
        .from("company")
        .update({ wms_provider: wmsProvider })
        .eq("id", company.id);
      if (error) throw error;
      message.success("Active WMS updated.");
      onRefresh();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setSavingWmsSwitch(false);
    }
  };

  const confirmWmsProviderChange = (newValue: "none" | "picqer" | "channeldock") => {
    const label = newValue === "none" ? "None" : newValue === "picqer" ? "Picqer" : "ChannelDock";
    Modal.confirm({
      title: "Change active WMS",
      content: `Are you sure you want to set the active WMS to ${label}? Orders, products, and stock will use this warehouse system.`,
      okText: "Change",
      cancelText: "Cancel",
      onOk: () => handleWmsProviderChange(newValue),
    });
  };

  const [fulfilmentCustomers, setFulfilmentCustomers] = useState<
    PicqerFulfilmentCustomer[]
  >([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Check if company is connected to Picqer
  const isConnected = company.picqer_enabled && company.picqer_api_key;
  const hasFullIntegration =
    isConnected && company.picqer_fulfilment_customer_id;

  useEffect(() => {
    if (showSetupModal) {
      loadFulfilmentCustomers();
    }
  }, [showSetupModal]);

  const fetchLinkedProductCounts = async () => {
    if (!company.id) return;
    try {
      const [picqerRes, channeldockRes, allRes] = await Promise.all([
        supabase
          .from("company_product")
          .select("id", { count: "exact", head: true })
          .eq("company_id", company.id)
          .not("picqer_product_id", "is", null),
        supabase
          .from("company_product")
          .select("id", { count: "exact", head: true })
          .eq("company_id", company.id)
          .not("channeldock_product_id", "is", null),
        supabase.from("company_product").select("id", { count: "exact", head: true }).eq("company_id", company.id),
      ]);
      setLinkedProductCounts({
        picqer: picqerRes.count ?? 0,
        channeldock: channeldockRes.count ?? 0,
      });
      setDashboardProductTotal(allRes.count ?? 0);
    } catch {
      setLinkedProductCounts({ picqer: 0, channeldock: 0 });
      setDashboardProductTotal(null);
    }
  };

  useEffect(() => {
    if (company.id) {
      fetchLinkedProductCounts();
    } else {
      setLinkedProductCounts(null);
      setDashboardProductTotal(null);
    }
  }, [company.id]);

  useEffect(() => {
    if (!showImportModal && company.id) {
      fetchLinkedProductCounts();
    }
  }, [showImportModal]);

  const fetchWmsTotalCounts = async () => {
    if (!company.id) return;
    setWmsTotalCountsLoading(true);
    setWmsTotalCounts({ picqer: null, channeldock: null });
    const next = { picqer: null as number | null, channeldock: null as number | null };
    try {
      if (company.picqer_enabled && company.picqer_api_key) {
        try {
          const { data } = await supabase.functions.invoke("picqer-product-import", {
            body: { action: "get_product_count", apiKey: company.picqer_api_key, companyId: company.id },
          });
          const res = data as { success?: boolean; count?: number };
          if (res?.success && typeof res?.count === "number") next.picqer = res.count;
        } catch {
          // leave null
        }
      }
      if (company.channeldock_enabled && company.channeldock_api_key) {
        try {
          // Same inventory scope as Import/Link (channeldock-product-import / center+seller when configured).
          const { data, error: cdFnError } = await supabase.functions.invoke("channeldock-product-import", {
            body: { action: "get_inventory_count", companyId: company.id },
          });
          if (cdFnError) console.warn("get_inventory_count", cdFnError);
          const res = data as { success?: boolean; count?: number };
          if (res?.success && typeof res?.count === "number") next.channeldock = res.count;
        } catch {
          // leave null
        }
      }
      setWmsTotalCounts(next);
    } finally {
      setWmsTotalCountsLoading(false);
    }
  };

  useEffect(() => {
    if (company.id) {
      fetchWmsTotalCounts();
    } else {
      setWmsTotalCounts({ picqer: null, channeldock: null });
      setWmsTotalCountsLoading(false);
    }
    // ChannelDock count uses center inventory scoped by channeldock_seller_id when configured — refetch when those change.
  }, [company.id, company.channeldock_seller_id, company.channeldock_enabled]);

  const fetchProductsListForModal = async (wms: "picqer" | "channeldock") => {
    if (!company.id) return;
    setProductsModalLoading(true);
    setProductsModalData(null);
    try {
      if (wms === "picqer" && company.picqer_api_key) {
        const { data } = await supabase.functions.invoke("picqer-product-import", {
          body: { action: "list_products", apiKey: company.picqer_api_key, companyId: company.id },
        });
        const res = data as { success?: boolean; data?: { products?: Array<{ idproduct: number; productcode: string; name: string; ean?: string }>; linkedProductIds?: number[] } };
        if (res?.success && res?.data) {
          setProductsModalData({
            products: (res.data.products ?? []).map((p) => ({
              id: p.idproduct,
              productcode: p.productcode,
              name: p.name,
              ean: p.ean,
            })),
            linkedIds: res.data.linkedProductIds ?? [],
          });
        }
      } else if (wms === "channeldock" && company.channeldock_api_key) {
        const { data, error: cdFnError } = await supabase.functions.invoke("channeldock-product-import", {
          body: { action: "list_inventory_products", companyId: company.id },
        });
        if (cdFnError) throw cdFnError;
        const res = data as { success?: boolean; data?: unknown[] };
        const rawProducts = Array.isArray(res?.data) ? res.data : [];
        const { data: linkedRows } = await supabase
          .from("company_product")
          .select("channeldock_product_id")
          .eq("company_id", company.id)
          .not("channeldock_product_id", "is", null);
        const linkedIds = (linkedRows ?? []).map((r: { channeldock_product_id: number }) => r.channeldock_product_id);
        const products = rawProducts.map((p: Record<string, unknown>) => ({
          id: typeof p.id === "number" ? p.id : Number(p.id) || 0,
          productcode: String(p.sku ?? p.productcode ?? p.ean ?? ""),
          name: String(p.title ?? p.name ?? ""),
          ean: p.ean != null ? String(p.ean) : undefined,
        }));
        setProductsModalData({ products, linkedIds });
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Failed to load products");
    } finally {
      setProductsModalLoading(false);
    }
  };

  const openProductsModal = (wms: "picqer" | "channeldock") => {
    setProductsModalWms(wms);
    fetchProductsListForModal(wms);
  };

  const loadFulfilmentCustomers = async () => {
    setLoadingCustomers(true);
    try {
      console.log("Loading fulfillment customers using general API key...");

      const result = await PicqerService.getFulfilmentCustomers();

      console.log("Fulfillment customers result:", result);

      if (result.success && result.data) {
        console.log(
          "Successfully loaded fulfillment customers:",
          result.data.length
        );
        setFulfilmentCustomers(result.data);
      } else {
        console.error("Failed to load fulfillment customers:", result.error);
        message.error(
          `Failed to load fulfillment customers: ${
            result.error || "Unknown error"
          }`
        );
        setFulfilmentCustomers([]);
      }
    } catch (error) {
      console.error("Error loading fulfillment customers:", error);
      message.error(
        "Failed to load fulfillment customers - please check console for details"
      );
      setFulfilmentCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleTestConnection = async () => {
    if (!company.picqer_api_key) {
      message.error("No API key configured");
      return;
    }

    setTestingConnection(true);
    try {
      const result = await PicqerService.testConnection(company.picqer_api_key);

      if (result.success) {
        message.success("Picqer connection test successful!");
      } else {
        message.error(`Connection test failed: ${result.error}`);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      message.error("Connection test failed");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSetupIntegration = () => {
    if (company.picqer_api_key) {
      // If API key exists, show fulfillment customer selection but don't populate the API key field
      form.setFieldsValue({
        apiKey: "", // Don't show the actual API key for security
        fulfilmentCustomerId: company.picqer_fulfilment_customer_id,
      });
      setShowSetupModal(true);
    } else {
      // If no API key, show setup form
      form.resetFields();
      setShowSetupModal(true);
    }
  };

  const handleSaveIntegration = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const result = await PicqerService.updateCompanyPicqerConnection(
        company.id!,
        {
          apiKey: values.apiKey,
          fulfilmentCustomerId: values.fulfilmentCustomerId || 0,
          enabled: true,
        }
      );

      if (result.success) {
        message.success("Picqer integration updated successfully!");
        setShowSetupModal(false);
        onRefresh();
      } else {
        message.error(`Failed to update integration: ${result.error}`);
      }
    } catch (error) {
      console.error("Error saving integration:", error);
      message.error("Failed to save integration settings");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: "Disconnect Picqer Integration",
      content:
        "Are you sure you want to disconnect this company from Picqer? This will disable purchase order creation and other Picqer features.",
      okText: "Disconnect",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const result = await PicqerService.disconnectCompanyPicqer(
            company.id!
          );

          if (result.success) {
            message.success("Picqer integration disconnected");
            onRefresh();
          } else {
            message.error(`Failed to disconnect: ${result.error}`);
          }
        } catch (error) {
          console.error("Error disconnecting:", error);
          message.error("Failed to disconnect integration");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const showApiKeyModal = () => {
    const keyName = `Dashboard - ${company.name || "Company"}`;
    setApiKeyName(keyName);
    setApiKeyModalVisible(true);
  };

  const handleGoToPicqer = () => {
    window.open(
      "https://e-freight-forwarding.picqer.com/config/apikeys/create",
      "_blank"
    );
    setApiKeyModalVisible(false);
  };

  const handleCopyKeyName = () => {
    navigator.clipboard.writeText(apiKeyName);
    message.success("API key name copied to clipboard!");
  };

  const activeWmsValue =
    company.wms_provider === "channeldock"
      ? "channeldock"
      : company.wms_provider === "picqer"
        ? "picqer"
        : "none";

  const runCdGetTest = async (key: CdGetEndpointKey) => {
    if (!company.id) return;
    setCdTestLoading(key);
    setCdTestResults((prev) => ({ ...prev, [key]: { success: false, error: "…" } }));
    try {
      let data: unknown;
      if (key === "list_orders") {
        const res = await supabase.functions.invoke("channeldock-orders", {
          body: { action: "list_orders", company_id: company.id },
        });
        if (res.error) throw res.error;
        data = res.data;
        const arr = (data as { data?: unknown[] })?.data;
        const count = Array.isArray(arr) ? arr.length : 0;
        const success = (data as { success?: boolean })?.success ?? false;
        const raw = { success, data: arr ?? (data as { data?: unknown })?.data, error: (data as { error?: string })?.error };
        setCdTestResults((prev) => ({ ...prev, [key]: { success, count: success ? count : undefined, error: success ? undefined : (data as { error?: string })?.error, raw } }));
      } else if (key === "list_products") {
        const res = await supabase.functions.invoke("channeldock-product-import", {
          body: { action: "list_inventory_products", companyId: company.id },
        });
        if (res.error) throw res.error;
        data = res.data;
        const arr = (data as { data?: unknown[] })?.data;
        const count = Array.isArray(arr) ? arr.length : 0;
        const success = (data as { success?: boolean })?.success ?? false;
        const raw = { success, data: arr ?? (data as { data?: unknown })?.data, error: (data as { error?: string })?.error };
        setCdTestResults((prev) => ({ ...prev, [key]: { success, count: success ? count : undefined, error: success ? undefined : (data as { error?: string })?.error, raw } }));
      } else {
        const res = await supabase.functions.invoke("channeldock-stock", {
          body: { companyId: company.id },
        });
        if (res.error) throw res.error;
        data = res.data;
        const arr = (data as { data?: unknown[] })?.data;
        const count = Array.isArray(arr) ? arr.length : 0;
        const success = (data as { success?: boolean })?.success ?? false;
        const raw = { success, data: arr ?? (data as { data?: unknown })?.data, error: (data as { error?: string })?.error };
        setCdTestResults((prev) => ({ ...prev, [key]: { success, count: success ? count : undefined, error: success ? undefined : (data as { error?: string })?.error, raw } }));
      }
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : "Request failed";
      setCdTestResults((prev) => ({ ...prev, [key]: { success: false, error: errMsg, raw: { success: false, error: errMsg } } }));
    } finally {
      setCdTestLoading(null);
    }
  };

  const cdResultLabels: Record<string, string> = {
    list_orders: "List orders",
    list_products: "List products (inventory)",
    stock: "Get stock",
  };

  return (
    <div>
      {/* Active WMS switch: which WMS this company uses */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Text strong>Active WMS</Text>
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Choose which warehouse system this company uses. You can add credentials for both; this switch selects the one in use.
          </Text>
          <Radio.Group
            value={activeWmsValue}
            onChange={(e) => confirmWmsProviderChange(e.target.value as "none" | "picqer" | "channeldock")}
            disabled={savingWmsSwitch}
          >
            <Space direction="vertical">
              <Radio value="none" disabled={savingWmsSwitch}>
                None
              </Radio>
              <Radio
                value="picqer"
                disabled={savingWmsSwitch || !hasPicqerCredentials}
              >
                Picqer
                {!hasPicqerCredentials && (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    (add credentials below)
                  </Text>
                )}
              </Radio>
              <Radio
                value="channeldock"
                disabled={savingWmsSwitch || !hasChannelDockCredentials}
              >
                ChannelDock
                {!hasChannelDockCredentials && (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    (add credentials below)
                  </Text>
                )}
              </Radio>
            </Space>
          </Radio.Group>
          {savingWmsSwitch && <Spin size="small" />}
        </Space>
      </Card>

      {!isConnected && (
        <Alert
          message="Picqer Not Connected"
          description="This company is not connected to Picqer. Set up the integration to enable purchase orders and inventory management."
          type="info"
          showIcon
          icon={<ApiOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}
      {isConnected && !hasFullIntegration && (
        <Alert
          message="Picqer Partially Connected"
          description="API key is configured but no fulfillment customer is selected. Some features may not work properly."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title={
          <Space>
            <img src="/images/picqer_logo.png" alt="Picqer" style={{ height: 24, width: "auto", display: "block" }} />
            Picqer Integration Settings
            {isConnected && <Tag color="green">Connected</Tag>}
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          {isConnected && (
            <>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleTestConnection}
                loading={testingConnection}
              >
                Test Connection
              </Button>
              <Button
                icon={<ImportOutlined />}
                onClick={() => {
                  setShowImportModal(true);
                  setImportModalWmsOverride("picqer");
                }}
              >
                Import/Link from Picqer
              </Button>
            </>
          )}
          <Button
            type="primary"
            icon={isConnected ? <SettingOutlined /> : <LinkOutlined />}
            onClick={handleSetupIntegration}
          >
            {isConnected ? "Configure" : "Connect to Picqer"}
          </Button>
          {isConnected && (
            <Button
              danger
              icon={<DisconnectOutlined />}
              onClick={handleDisconnect}
              loading={loading}
            >
              Disconnect
            </Button>
          )}
        </Space>
        {isConnected ? (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="API Key" span={2}>
              <Text code>
                {company.picqer_api_key
                  ? `${company.picqer_api_key.substring(0, 8)}...`
                  : "Not configured"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color="green">Connected</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Enabled">
              {company.picqer_enabled ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="red">No</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Fulfillment Customer ID" span={2}>
              {company.picqer_fulfilment_customer_id ? (
                <Text strong>#{company.picqer_fulfilment_customer_id}</Text>
              ) : (
                <Text type="secondary">Not configured</Text>
              )}
            </Descriptions.Item>
            {dashboardProductTotal != null && (
              <Descriptions.Item label="Total my products (dashboard)" span={2}>
                <Text strong>{dashboardProductTotal}</Text>
                <Text type="secondary"> product{dashboardProductTotal !== 1 ? "s" : ""}</Text>
              </Descriptions.Item>
            )}
            {linkedProductCounts != null && (
              <Descriptions.Item label="My Products linked" span={2}>
                <Text strong>{linkedProductCounts.picqer}</Text>
                <Text type="secondary"> product{linkedProductCounts.picqer !== 1 ? "s" : ""}</Text>
              </Descriptions.Item>
            )}
            {(wmsTotalCountsLoading || wmsTotalCounts.picqer != null) && (
              <Descriptions.Item label="Products in Picqer" span={2}>
                {wmsTotalCountsLoading && wmsTotalCounts.picqer == null ? (
                  <Spin size="small" />
                ) : wmsTotalCounts.picqer != null ? (
                  <Text
                    type="secondary"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => openProductsModal("picqer")}
                  >
                    <Text strong>{wmsTotalCounts.picqer}</Text>
                    {" product"}{wmsTotalCounts.picqer !== 1 ? "s" : ""}
                  </Text>
                ) : null}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#666" }}
          >
            <ApiOutlined
              style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
            />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No Picqer Integration
            </div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>
              Connect this company to Picqer to enable purchase orders,
              inventory sync, and fulfillment features.
            </div>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              onClick={handleSetupIntegration}
            >
              Set Up Picqer Integration
            </Button>
          </div>
        )}
      </Card>

      {/* Setup/Configuration Modal */}
      <Modal
        title={
          isConnected
            ? "Configure Picqer Integration"
            : "Set Up Picqer Integration"
        }
        open={showSetupModal}
        onCancel={() => setShowSetupModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowSetupModal(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={handleSaveIntegration}
          >
            {isConnected ? "Update Integration" : "Connect to Picqer"}
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="apiKey"
            label="Picqer API Key"
            rules={[
              { required: true, message: "Please enter your Picqer API key" },
              { min: 10, message: "API key should be at least 10 characters" },
            ]}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input.Password
                placeholder="Enter your Picqer API key"
                autoComplete="off"
                visibilityToggle={{ visible: false }}
                defaultValue=""
              />
              <Button
                type="link"
                icon={<KeyOutlined />}
                onClick={showApiKeyModal}
                style={{ padding: 0, height: "auto" }}
              >
                Create new API key
              </Button>
            </Space>
          </Form.Item>

          <Form.Item
            name="fulfilmentCustomerId"
            label="Fulfillment Customer"
            help="Select the fulfillment customer for this company in Picqer"
          >
            <Select
              placeholder="Select fulfillment customer"
              loading={loadingCustomers}
              showSearch
              optionFilterProp="children"
              onFocus={() => {
                if (fulfilmentCustomers.length === 0) {
                  loadFulfilmentCustomers();
                }
              }}
            >
              {fulfilmentCustomers
                .filter((customer) => customer.idfulfilment_customer)
                .map((customer) => (
                  <Option
                    key={customer.idfulfilment_customer}
                    value={customer.idfulfilment_customer}
                  >
                    <Space>
                      <Text key="name" strong>
                        {customer.name}
                      </Text>
                      <Text key="id" type="secondary">
                        #{customer.idfulfilment_customer}
                      </Text>
                      {customer.active && (
                        <Tag key="status" color="green">
                          Active
                        </Tag>
                      )}
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ChannelDock Integration */}
      <ChannelDockIntegrationCard
        company={company}
        onRefresh={onRefresh}
        linkedProductCount={linkedProductCounts?.channeldock ?? undefined}
        dashboardProductTotal={dashboardProductTotal ?? undefined}
        wmsTotalCount={wmsTotalCounts.channeldock ?? undefined}
        wmsTotalCountLoading={wmsTotalCountsLoading}
        onProductsCountClick={
          wmsTotalCounts.channeldock != null ? () => openProductsModal("channeldock") : undefined
        }
        onImportProducts={() => {
          setShowImportModal(true);
          setImportModalWmsOverride("channeldock");
        }}
      />

      {/* ChannelDock seller GET endpoints – test table (only when CD credentials exist) */}
      {hasChannelDockCredentials && company.id && (
        <Card size="small" title="ChannelDock seller API – GET endpoints" style={{ marginTop: 16 }}>
          <Table
            size="small"
            pagination={false}
            dataSource={[
              { key: "list_orders", endpoint: "channeldock-orders", action: "List orders", label: "List orders" },
              { key: "list_products", endpoint: "channeldock-orders", action: "List products", label: "List products (inventory)" },
              { key: "stock", endpoint: "channeldock-stock", action: "Get stock", label: "Get stock" },
            ]}
            columns={[
              { title: "Endpoint", dataIndex: "label", key: "endpoint", width: 200, render: (label: string) => label },
              {
                title: "Test",
                key: "test",
                width: 100,
                render: (_: unknown, row: { key: CdGetEndpointKey }) => (
                  <Button
                    size="small"
                    onClick={() => runCdGetTest(row.key)}
                    loading={cdTestLoading === row.key}
                  >
                    Test
                  </Button>
                ),
              },
              {
                title: "Result",
                key: "result",
                render: (_: unknown, row: { key: string }) => {
                  const r = cdTestResults[row.key];
                  if (!r) return <Text type="secondary">—</Text>;
                  const label = r.success ? (
                    <Tag color="green" style={{ cursor: "pointer" }} onClick={() => setShowCdResultModal(true)}>
                      OK – {r.count ?? 0} items
                    </Tag>
                  ) : (
                    <Text type="danger" style={{ cursor: "pointer" }} onClick={() => setShowCdResultModal(true)}>
                      {r.error ?? "Failed"}
                    </Text>
                  );
                  return label;
                },
              },
            ]}
          />
        </Card>
      )}

      {/* ChannelDock GET results – JSON modal with collapse per result */}
      <Modal
        title="ChannelDock GET responses"
        open={showCdResultModal}
        onCancel={() => setShowCdResultModal(false)}
        footer={[<Button key="close" onClick={() => setShowCdResultModal(false)}>Close</Button>]}
        width={720}
        destroyOnClose
      >
        <Collapse
          defaultActiveKey={Object.keys(cdTestResults)}
          items={Object.entries(cdTestResults).map(([key, r]) => ({
            key,
            label: `${cdResultLabels[key] ?? key} ${r.success ? `✓ ${r.count ?? 0} items` : "✗"}`,
            children: (
              <pre style={{ margin: 0, fontSize: 12, maxHeight: 360, overflow: "auto", background: "#f5f5f5", padding: 12, borderRadius: 4 }}>
                {JSON.stringify(r.raw ?? { success: r.success, error: r.error }, null, 2)}
              </pre>
            ),
          }))}
        />
      </Modal>

      {/* Products in WMS list modal */}
      <Modal
        title={productsModalWms === "channeldock" ? "Products in ChannelDock" : "Products in Picqer"}
        open={productsModalWms != null}
        onCancel={() => {
          setProductsModalWms(null);
          setProductsModalData(null);
        }}
        footer={[
          <Button key="close" onClick={() => { setProductsModalWms(null); setProductsModalData(null); }}>
            Close
          </Button>,
        ]}
        width={800}
        destroyOnClose
      >
        {productsModalLoading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : productsModalData ? (
          <Table
            size="small"
            dataSource={productsModalData.products}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `Total ${t} products` }}
            columns={[
              { title: "ID", dataIndex: "id", key: "id", width: 90, render: (v: number) => `#${v}` },
              { title: "Code", dataIndex: "productcode", key: "productcode", ellipsis: true },
              { title: "Name", dataIndex: "name", key: "name", ellipsis: true },
              { title: "EAN", dataIndex: "ean", key: "ean", width: 140 },
              {
                title: "Linked",
                key: "linked",
                width: 90,
                render: (_: unknown, record: { id: number }) =>
                  productsModalData.linkedIds.includes(record.id) ? (
                    <Tag color="green">Yes</Tag>
                  ) : (
                    <Tag>No</Tag>
                  ),
              },
            ]}
          />
        ) : null}
      </Modal>

      {/* Product Import Modal */}
      <PicqerProductImport
        visible={showImportModal}
        onCancel={() => {
          setShowImportModal(false);
          setImportModalWmsOverride(null);
        }}
        onSuccess={() => {
          setShowImportModal(false);
          setImportModalWmsOverride(null);
          onRefresh();
        }}
        company={company}
        wmsProviderOverride={importModalWmsOverride ?? undefined}
      />

      {/* API Key Creation Modal */}
      <Modal
        title="Create New API Key"
        open={apiKeyModalVisible}
        onCancel={() => setApiKeyModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setApiKeyModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="copy" onClick={handleCopyKeyName}>
            Copy Key Name
          </Button>,
          <Button key="picqer" type="primary" onClick={handleGoToPicqer}>
            Go to Picqer
          </Button>,
        ]}
      >
        <div>
          <p>Use this name for your new API key:</p>
          <Input value={apiKeyName} readOnly style={{ marginBottom: 16 }} />
          <p style={{ fontSize: "12px", color: "#666" }}>
            Click "Copy Key Name" to copy the name, then click "Go to Picqer" to
            create the API key.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPicqerIntegrationSection;
